// Varasun Technologies — structured site content.
// Edit this file to update services, industries, insights and stats
// without touching any component code.

export const BRAND = {
    name: "Varasun Technologies",
    tagline: "Rising with Innovation. Leading with Technology.",
    email: "info@varasuntechnologies.com",
    emails: {
        general: "info@varasuntechnologies.com",
        sales: "sales@varasuntechnologies.com",
        support: "support@varasuntechnologies.com",
        hr: "hr@varasuntechnologies.com",
        finance: "finance@varasuntechnologies.com",
        admin: "admin@varasuntechnologies.com",
    },
    entities: [
        { name: "Varasun Technologies Inc.", region: "United States" },
        { name: "Varasun Technologies India Pvt Ltd", region: "India" },
    ],
};

export const CAPABILITIES = [
    {
        code: "CAP-01",
        icon: "Brain",
        title: "AI & Data Solutions",
        tag: "High Impact",
        outcome: "Turn fragmented data into governed, production-ready AI that drives real decisions.",
        visual: "/visuals/visual-ai-data-16x9.webp",
        desc: "AI strategy, generative AI, machine learning, modern data platforms, analytics and AI governance — from experimentation to production outcomes.",
        points: ["AI Strategy & Roadmaps", "Generative AI & RAG", "Data Platforms & Analytics", "AI Governance"],
    },
    {
        code: "CAP-02",
        icon: "Cloud",
        title: "Cloud & Infrastructure",
        tag: "Core",
        outcome: "Scale reliably and cut cloud waste with secure, well-architected infrastructure.",
        visual: "/visuals/visual-cloud-16x9.webp",
        desc: "Cloud transformation, migration, hybrid architecture, infrastructure modernization, FinOps optimization and disaster recovery.",
        points: ["Cloud Migration", "Hybrid & Multi-Cloud", "Infrastructure Modernization", "FinOps & Optimization"],
    },
    {
        code: "CAP-03",
        icon: "ShieldCheck",
        title: "Cybersecurity",
        tag: "Mission Critical",
        outcome: "Reduce cyber risk and strengthen resilience with security engineered in from day one.",
        visual: "/visuals/visual-cyber-16x9.webp",
        desc: "Security strategy, identity, Zero Trust architecture, 24/7 monitoring, risk management and compliance — security by design, not afterthought.",
        points: ["Zero Trust Architecture", "Identity & Access", "Security Monitoring", "Risk & Compliance"],
    },
    {
        code: "CAP-04",
        icon: "Laptop",
        title: "Digital Workplace",
        tag: "Enterprise",
        outcome: "Give employees a secure, modern Microsoft 365 workplace that lifts productivity.",
        visual: "/visuals/visual-workplace-16x9.webp",
        desc: "Modern workplace transformation — collaboration, Microsoft 365, endpoint management and employee experience that actually works.",
        points: ["Modern Workplace", "Collaboration & M365", "Endpoint Management", "Employee Experience"],
    },
    {
        code: "CAP-05",
        icon: "Server",
        title: "Managed IT Services",
        tag: "Continuous",
        outcome: "Reduce downtime and free your team with 24/7 monitoring, NOC, SOC and service desk.",
        visual: "/visuals/visual-managed-16x9.webp",
        desc: "24/7 monitoring, infrastructure and cloud operations, security operations, service desk and managed applications.",
        points: ["24/7 Monitoring & SRE", "Cloud Operations", "Security Operations", "Service Desk"],
    },
    {
        code: "CAP-06",
        icon: "Compass",
        title: "Technology Consulting",
        tag: "Strategic",
        outcome: "Make confident technology decisions with senior, engineering-grounded advice.",
        visual: "/visuals/visual-transformation-16x9.webp",
        desc: "IT strategy, architecture, transformation roadmaps and technology assessments — advisory grounded in engineering reality.",
        points: ["IT Strategy & CIO Advisory", "Enterprise Architecture", "Transformation Roadmaps", "Technology Assessment"],
    },
];

export const CHALLENGES = [
    {
        title: "Legacy infrastructure is holding the business back",
        symptom: "High maintenance costs, slow release cycles and systems nobody dares to touch.",
        answer: "Structured modernization — assess, migrate, refactor — without disrupting daily operations.",
    },
    {
        title: "Cloud complexity keeps compounding",
        symptom: "Sprawling estates, unpredictable bills and architectures that grew without a plan.",
        answer: "Deliberate cloud architecture with FinOps discipline and measurable cost reduction.",
    },
    {
        title: "Cybersecurity threats are outpacing defenses",
        symptom: "Expanding attack surfaces, fragmented tooling and mounting compliance pressure.",
        answer: "Zero Trust blueprints with 24/7 monitoring and compliance built into the design.",
    },
    {
        title: "Data is fragmented and AI-ready nowhere",
        symptom: "Siloed data, no governance, and AI pilots that never reach production.",
        answer: "A governed data foundation that makes AI deployment repeatable and safe.",
    },
    {
        title: "Skills gaps slow every initiative",
        symptom: "Scarce specialists, overloaded teams and roadmaps that slip quarter after quarter.",
        answer: "Dual-shore engineering scale — senior architects onshore, deep delivery teams offshore.",
    },
];

export const METHODOLOGY = [
    { num: "01", title: "Discover", desc: "Understand the business, the technology landscape and the objectives that matter." },
    { num: "02", title: "Strategize", desc: "Build the transformation roadmap — sequenced, costed and tied to outcomes." },
    { num: "03", title: "Design", desc: "Architect the future state: systems, security boundaries and operating model." },
    { num: "04", title: "Implement", desc: "Execute with dual-shore engineering sprints, continuous integration and testing." },
    { num: "05", title: "Optimize", desc: "Tune performance, security and cost until the numbers hold." },
    { num: "06", title: "Operate", desc: "Run it — 24/7 managed operations, proactive monitoring, continuous evolution." },
];

export const STATS = [
    { value: 15, suffix: "+", decimals: 0, label: "Years of enterprise delivery experience" },
    { value: 150, suffix: "+", decimals: 0, label: "Transformation projects delivered" },
    { value: 99.9, suffix: "%", decimals: 1, label: "Managed environment availability" },
    { value: 2, suffix: "", decimals: 0, label: "Delivery operations across time zones" },
];

export const OFFICES = [
    {
        entity: "Varasun Technologies Inc.",
        region: "United States — Headquarters",
        detail: "Enterprise strategy, client advisory, program leadership & delivery operations",
    },
];

export const SERVICE_OPTIONS = CAPABILITIES.map((c) => c.title);
export const BUDGET_OPTIONS = ["Under $50k", "$50k – $100k", "$100k – $250k", "$250k – $500k", "$500k+"];
export const TIMELINE_OPTIONS = ["Immediate (0–30 days)", "This quarter (1–3 months)", "Planning phase (3–6 months)", "Exploratory"];
export const COUNTRY_OPTIONS = ["United States", "India", "United Kingdom", "Canada", "UAE", "Singapore", "Australia", "Germany", "Other"];
